import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import type { DistribucionSlice } from '@/contabilidad/utils/aggregations';
import type { Moneda } from '@/contabilidad/types';
import { formatCurrencyCompact } from '@/contabilidad/utils/formatters';

export const INGRESOS_PALETTE = ['#3182CE', '#63B3ED', '#90CDF4', '#A0AEC0', '#CBD5E0'];
export const EGRESOS_PALETTE  = ['#2D3748', '#4A5568', '#718096', '#A0AEC0', '#CBD5E0', '#E2E8F0'];

interface DistribucionChartProps {
  data: DistribucionSlice[];
  palette: string[];
  moneda: Moneda;
}

const RADIAN = Math.PI / 180;

function SliceLabel(props: PieLabelRenderProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!cx || !cy || !midAngle || !innerRadius || !outerRadius || !percent || percent < 0.08) return null;
  const radius = (Number(innerRadius) + Number(outerRadius)) / 2;
  const x = Number(cx) + radius * Math.cos(-Number(midAngle) * RADIAN);
  const y = Number(cy) + radius * Math.sin(-Number(midAngle) * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 600 }}
    >
      {`${(Number(percent) * 100).toFixed(0)}%`}
    </text>
  );
}

export function DistribucionChart({ data, palette, moneda }: DistribucionChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[180px] text-xs text-muted-foreground">
        Sin datos disponibles
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      {/* Donut */}
      <div className="shrink-0" style={{ width: 160, height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={45}
              outerRadius={72}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              labelLine={false}
              label={SliceLabel}
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={palette[index % palette.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [formatCurrencyCompact(value, moneda), 'Total']}
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '12px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Leyenda */}
      <ul className="flex-1 space-y-1.5 min-w-0">
        {data.map((slice, index) => (
          <li key={slice.name} className="flex items-center gap-2 text-xs min-w-0">
            <span
              className="size-2.5 rounded-full shrink-0"
              style={{ backgroundColor: palette[index % palette.length] }}
            />
            <span className="truncate text-foreground">{slice.name}</span>
            <span className="ml-auto font-medium text-muted-foreground shrink-0">
              {slice.porcentaje}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
